import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, Select, Typography } from "antd";
import SessionManager from "@Core/session";
import { useQuery } from "@tanstack/react-query";
import CreditService from "@Services/CreditService";
import { QueryKeys, ThreeMinutes } from "@Constants";
import { MainContext } from "@Context/provider";
import { ICreditDetails } from "@Models/ICreditDetails";
import { ViewMode } from "@Context/state";
import { Inventory2Outlined } from "@mui/icons-material";
import { isPresent } from "@Utils";
const { Option } = Select;

export const CreditsBalance: React.FC = () => {
    const { Text } = Typography;
    const { ui: uiState, setCurrentCredit } = useContext(MainContext);
    const [credits, setCredits] = useState<ICreditDetails[]>([]);

    const creditQuery = useQuery(
        [QueryKeys.UserCredits],
        async () => {
            const service = new CreditService();
            const credits = await service.getUserFull(SessionManager.getServiceUser()?.userId);
            if (!credits.hasErrors) {
                return credits.value;
            }
            return [];
        },
        {
            enabled: !!SessionManager.getServiceUser(),
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

    const getCurrent = (): ICreditDetails => {
        if (credits.length === 0) return null;
        const index = credits.findIndex((x) => x.creditId === uiState.currentCredit?.instance);
        return creditQuery?.data[index === -1 ? 0 : index];
    };

    const switchCredit = (value: string) => {
        if (credits.length > 0) {
            setCurrentCredit({ instance: value });
        }
    };

    const inactive = getCurrent()?.drawDownValue === 0 ? "inactive" : "";

    useEffect(() => {
        if (isPresent(creditQuery?.data)) {
            setCredits(creditQuery?.data);

            // If the current credit is not in the list, set the first credit as the current credit
            const hasUiStateCredit = creditQuery?.data?.some((x) => x.creditId === uiState.currentCredit?.instance);
            if (!hasUiStateCredit && creditQuery?.data?.length > 0) {
                setCurrentCredit({ instance: creditQuery?.data[0]?.creditId });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creditQuery?.data]);

    return (
        credits.length > 0 && (
            <div style={{ display: "flex" }}>
                <Popover
                    trigger="hover"
                    content={
                        <>
                            <h3>{getCurrent()?.description}</h3>
                            <table className={"link-item-table"}>
                                <tbody>
                                    <tr>
                                        <td className={"td-left"}>Totum credits</td>
                                        <td className={"td-right"}>{getCurrent()?.creditValue}</td>
                                    </tr>
                                    <tr>
                                        <td className={"td-left"}>Shipments remaining</td>
                                        <td className={"td-right"}>{getCurrent()?.drawDownValue}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </>
                    }
                >
                    <Link className="link-item" to="/supply-suite">
                        <Inventory2Outlined />
                        <Text style={{ color: "white", fontSize: "15px" }}>
                            {uiState.mode === ViewMode.desktop ? <span>Supply suite</span> : null}
                            <span className={`link-item-text ${inactive}`}>{`(${getCurrent()?.creditValue})`}</span>
                        </Text>
                    </Link>
                </Popover>
                {creditQuery?.data?.length > 1 && (
                    <Select
                        className="credits-select"
                        style={{ maxWidth: "160px", textOverflow: "ellipsis", color: "white" }}
                        bordered={false}
                        defaultValue={uiState.currentCredit?.instance}
                        onChange={(val) => switchCredit(val)}
                    >
                        {creditQuery?.data?.map((x, i) => (
                            <Option key={x.creditId} value={x.creditId}>
                                {x.description}
                            </Option>
                        ))}
                    </Select>
                )}
            </div>
        )
    );
};
export default CreditsBalance;
